import { useEffect } from "react";
import { usePlatformBrand } from "@/components/app/PlatformBrandProvider";

// Per-route SEO hook. Updates document.title, meta description,
// canonical URL, and Open Graph / Twitter tags on mount; restores
// the previous values on unmount so route changes stay clean.
//
// Call from the top of any page component:
//
//   usePageTitle("Sign in");                      // title only
//   usePageSeo({                                  // full metadata
//     title: "Pricing",
//     description: "Transparent per-seat pricing for UK colleges.",
//     path: "/pricing",
//   });
//
// The title shape is always "<page> · ExamBase". The description
// is capped to ~160 chars to match Google's snippet length. The
// canonical URL is always absolute under https://exambase.uk.

const SITE_ORIGIN = "https://exambase.uk";
const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/og-image.svg`;

function setMeta(name: string, content: string, attr: "name" | "property" = "name") {
  let el = document.head.querySelector<HTMLMetaElement>(
    `meta[${attr}="${name}"]`,
  );
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  return el;
}

function setLinkRel(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  return el;
}

export function usePageTitle(title: string): void {
  const { name } = usePlatformBrand();
  useEffect(() => {
    const previous = document.title;
    document.title = `${title} · ${name}`;
    return () => {
      document.title = previous;
    };
  }, [title, name]);
}

export interface PageSeoInput {
  title: string;
  description?: string;
  /** Absolute path (e.g. "/pricing"). Converted to a canonical URL. */
  path?: string;
  /** Absolute URL of a 1200×630 share image. Optional — defaults to /og-image.svg. */
  image?: string;
  /** Override the "og:type" (default "website"). */
  type?: "website" | "article";
  /** Set to true to discourage indexing (staging routes etc.). */
  noindex?: boolean;
}

export function usePageSeo(input: PageSeoInput): void {
  const {
    title,
    description,
    path,
    image = DEFAULT_OG_IMAGE,
    type = "website",
    noindex = false,
  } = input;
  const { name } = usePlatformBrand();

  useEffect(() => {
    const previousTitle = document.title;
    const fullTitle = `${title} · ${name}`;
    document.title = fullTitle;

    const url = path ? `${SITE_ORIGIN}${path}` : window.location.href;
    const snapshots: Array<() => void> = [() => {
      document.title = previousTitle;
    }];

    if (description) {
      const prev = document.head
        .querySelector<HTMLMetaElement>('meta[name="description"]')
        ?.getAttribute("content");
      setMeta("description", description);
      snapshots.push(() => {
        if (prev === null || prev === undefined)
          document.head
            .querySelector('meta[name="description"]')
            ?.removeAttribute("content");
        else setMeta("description", prev);
      });
      setMeta("og:description", description, "property");
      setMeta("twitter:description", description);
    }

    setMeta("og:title", fullTitle, "property");
    setMeta("og:url", url, "property");
    setMeta("og:type", type, "property");
    setMeta("og:site_name", name, "property");
    setMeta("og:image", image, "property");
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", fullTitle);
    setMeta("twitter:image", image);

    setLinkRel("canonical", url);

    if (noindex) {
      setMeta("robots", "noindex, nofollow");
      snapshots.push(() => setMeta("robots", "index, follow"));
    } else {
      setMeta("robots", "index, follow");
    }

    return () => {
      snapshots.forEach((fn) => fn());
    };
  }, [title, description, path, image, type, noindex, name]);
}
